import { useEffect, useMemo, useState } from 'react';
import {
  ArrowClockwiseIcon,
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  ClockIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { formatFileSize } from '@/lib/pricelist-utils';
import type {
  ProjectUploadStatus,
  ProjectUploadTask,
} from './useProjectUploadQueue';

interface ProjectUploadTrayProps {
  tasks: ProjectUploadTask[];
  cancel: (taskId: string) => void;
  retry: (taskId: string) => void;
}

function isActive(status: ProjectUploadStatus): boolean {
  return (
    status === 'queued' || status === 'uploading' || status === 'finalizing'
  );
}

function taskPercent(task: ProjectUploadTask): number {
  if (task.status === 'succeeded') return 100;
  if (task.totalBytes === 0) return 0;
  return Math.round((task.loadedBytes / task.totalBytes) * 100);
}

function StatusIcon({ status }: { status: ProjectUploadStatus }) {
  if (status === 'queued')
    return <ClockIcon className="size-4 shrink-0 text-muted-foreground" />;
  if (status === 'uploading' || status === 'finalizing')
    return <CircleNotchIcon className="size-4 shrink-0 animate-spin" />;
  if (status === 'succeeded')
    return <CheckCircleIcon className="size-4 shrink-0 text-green-600" />;
  if (status === 'failed')
    return <WarningCircleIcon className="size-4 shrink-0 text-destructive" />;
  return <XIcon className="size-4 shrink-0 text-muted-foreground" />;
}

function TaskStatus({ task }: { task: ProjectUploadTask }) {
  if (task.status === 'queued')
    return <T context="project upload waiting status">Waiting…</T>;
  if (task.status === 'finalizing')
    return <T context="project upload finalizing status">Saving file…</T>;
  if (task.status === 'succeeded')
    return <T context="project upload succeeded status">Upload complete</T>;
  if (task.status === 'failed')
    return (
      <span className="text-destructive">
        {task.errorMessage ?? (
          <T context="project upload failed status">Upload failed</T>
        )}
      </span>
    );
  if (task.status === 'cancelled')
    return <T context="project upload cancelled status">Cancelled</T>;
  return (
    <T context="project upload transferred bytes status">
      <Var>{formatFileSize(task.loadedBytes)}</Var> of{' '}
      <Var>{formatFileSize(task.totalBytes)}</Var>
    </T>
  );
}

function UploadTaskRow({
  task,
  cancel,
  retry,
  dismiss,
}: {
  task: ProjectUploadTask;
  cancel: (taskId: string) => void;
  retry: (taskId: string) => void;
  dismiss: (taskId: string) => void;
}) {
  const gt = useGT();
  const active = isActive(task.status);
  const canCancel = task.status === 'queued' || task.status === 'uploading';
  const percent = taskPercent(task);
  return (
    <div className="flex flex-col gap-1.5 px-3 py-2">
      <div className="flex items-center gap-2">
        <StatusIcon status={task.status} />
        <span
          className="min-w-0 flex-1 truncate text-sm"
          title={task.file.name}
        >
          {task.file.name}
        </span>
        {task.status === 'failed' && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            aria-label={gt('Retry upload', {
              context: 'retry a failed project upload button',
            })}
            onClick={() => retry(task.id)}
          >
            <ArrowClockwiseIcon />
          </Button>
        )}
        {canCancel && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            aria-label={gt('Cancel upload', {
              context: 'cancel an active project upload button',
            })}
            onClick={() => cancel(task.id)}
          >
            <XIcon />
          </Button>
        )}
        {!active && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            aria-label={gt('Dismiss upload', {
              context: 'hide a finished project upload row',
            })}
            onClick={() => dismiss(task.id)}
          >
            <XIcon />
          </Button>
        )}
      </div>
      {(task.status === 'uploading' || task.status === 'finalizing') && (
        <Progress value={percent} className="h-1.5" />
      )}
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="min-w-0 truncate">
          <TaskStatus task={task} />
        </span>
        {task.status === 'uploading' && (
          <span className="shrink-0 tabular-nums">
            <T context="project upload percentage status">
              <Var>{percent}</Var>%
            </T>
          </span>
        )}
      </div>
    </div>
  );
}

export function ProjectUploadTray({
  tasks,
  cancel,
  retry,
}: ProjectUploadTrayProps) {
  const gt = useGT();
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const activeIds = new Set(
      tasks.filter((task) => isActive(task.status)).map((task) => task.id),
    );
    if (activeIds.size === 0) return;
    setDismissed((current) => {
      const next = new Set(
        Array.from(current).filter((taskId) => !activeIds.has(taskId)),
      );
      return next.size === current.size ? current : next;
    });
  }, [tasks]);

  const visibleTasks = useMemo(
    () => tasks.filter((task) => !dismissed.has(task.id)),
    [dismissed, tasks],
  );
  if (visibleTasks.length === 0) return null;

  const activeCount = visibleTasks.filter((task) =>
    isActive(task.status),
  ).length;
  const hasFinished = visibleTasks.some((task) => !isActive(task.status));
  const dismiss = (taskId: string) =>
    setDismissed((current) => new Set([...current, taskId]));
  const clearFinished = () =>
    setDismissed(
      (current) =>
        new Set([
          ...current,
          ...visibleTasks
            .filter((task) => !isActive(task.status))
            .map((task) => task.id),
        ]),
    );

  return (
    <section
      className="absolute bottom-3 right-3 w-80 overflow-hidden rounded-lg border bg-popover shadow-lg"
      aria-label={gt('Project uploads', {
        context: 'accessible name for project upload progress tray',
      })}
    >
      <div className="flex items-center gap-2 border-b bg-muted/50 px-3 py-2">
        <CircleNotchIcon
          className={cn('size-4 shrink-0', activeCount > 0 && 'animate-spin')}
        />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          <T context="project upload tray heading">
            Uploads (<Var>{visibleTasks.length}</Var>)
          </T>
        </span>
        {hasFinished && (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto shrink-0 px-1 py-0 text-xs"
            onClick={clearFinished}
          >
            <T context="clear finished project uploads button">
              Clear finished
            </T>
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          aria-label={
            collapsed
              ? gt('Expand project uploads', {
                  context: 'expand project upload tray button',
                })
              : gt('Collapse project uploads', {
                  context: 'collapse project upload tray button',
                })
          }
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((current) => !current)}
        >
          {collapsed ? <CaretUpIcon /> : <CaretDownIcon />}
        </Button>
      </div>
      {!collapsed && (
        <div className="max-h-72 divide-y overflow-y-auto">
          {visibleTasks.map((task) => (
            <UploadTaskRow
              key={task.id}
              task={task}
              cancel={cancel}
              retry={retry}
              dismiss={dismiss}
            />
          ))}
        </div>
      )}
    </section>
  );
}
